import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { assetService } from '../services/assetService';
import { handoffUploadedAssets } from '../services/extractionHandoff';
import { mergeUploadedAssetIntoHomeStore } from '../services/homeAssetDayStore';
import { invalidateHomeCollectionStats } from '../services/homeCollectionStatsService';
import type { Asset } from '../types/domain';

export function AssetUploadPage() {
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmAsset, setPendingConfirmAsset] = useState<Asset | null>(null);

  async function finishAfterHandoff(asset: Asset, handoff: Awaited<ReturnType<typeof handoffUploadedAssets>>) {
    mergeUploadedAssetIntoHomeStore(user!.id, activeSubject, asset);
    invalidateHomeCollectionStats(user!.id);

    if (handoff.pasSkippedIds.includes(asset.id)) {
      setError('已上传；检测到答题纸特征，已跳过自动提取。请走实战答题纸通道，或在详情页确认后仍要提取。');
      setPendingConfirmAsset(null);
      navigate(`/assets/${asset.id}`);
      return;
    }

    if (handoff.startedIds.includes(asset.id)) {
      setPendingConfirmAsset(null);
      navigate(`/assets/${asset.id}`);
      return;
    }

    setPendingConfirmAsset(asset);
    setError('网络不佳：图片已上传，但提取入队未确认。可重试确认入队（不会重新上传）。');
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!user?.id || !file) return;
    setSubmitting(true);
    setError(null);
    setPendingConfirmAsset(null);
    try {
      const asset = await assetService.uploadImageAsset({
        userId: user.id,
        file,
        description,
        subject: activeSubject,
      });
      setSubmitting(false);
      setConfirming(true);
      const handoff = await handoffUploadedAssets(user.id, [asset]);
      setConfirming(false);
      await finishAfterHandoff(asset, handoff);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '上传失败');
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  }

  async function handleConfirmEnqueue() {
    if (!user?.id || !pendingConfirmAsset) return;
    setConfirming(true);
    setError(null);
    try {
      const handoff = await handoffUploadedAssets(user.id, [pendingConfirmAsset]);
      await finishAfterHandoff(pendingConfirmAsset, handoff);
      if (handoff.startedIds.includes(pendingConfirmAsset.id)) {
        setError(null);
      } else if (!handoff.pasSkippedIds.includes(pendingConfirmAsset.id)) {
        setError('仍未确认入队，请检查网络后重试，或稍后在素材详情确认。');
      }
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : '确认入队失败');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="text-2xl font-semibold text-ink">上传素材</h1>
      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card">
        <label className="block text-sm">
          <span className="text-muted">图片文件</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-sm"
            disabled={submitting || confirming}
          />
        </label>
        <label className="block text-sm">
          <span className="text-muted">描述（可选）</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-lg border border-border px-3 py-2"
            disabled={submitting || confirming}
          />
        </label>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={!file || submitting || confirming || Boolean(pendingConfirmAsset)}
            className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
          >
            {submitting ? '上传中…' : confirming && !pendingConfirmAsset ? '提交提取…' : '上传'}
          </button>
          {pendingConfirmAsset ? (
            <button
              type="button"
              disabled={confirming}
              onClick={() => void handleConfirmEnqueue()}
              className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
            >
              {confirming ? '确认中…' : '确认入队'}
            </button>
          ) : null}
          {pendingConfirmAsset ? (
            <button
              type="button"
              disabled={confirming}
              onClick={() => navigate(`/assets/${pendingConfirmAsset.id}`)}
              className="rounded-lg border border-border px-4 py-2 text-muted"
            >
              稍后去详情
            </button>
          ) : (
            <Link to="/assets" className="rounded-lg border border-border px-4 py-2 text-muted">取消</Link>
          )}
        </div>
      </form>
      <p className="text-sm text-muted">
        P2：也可在
        <Link to="/scan" className="text-primary"> 图片导入 </Link>
        页面上传替代相机扫描。
      </p>
    </div>
  );
}
